import { Alert, Button, Spin, Tag } from "antd";
import { AlertCircle, CheckCircle2, ExternalLink, FileText, Shield, Upload } from "lucide-react";
import { useMemo } from "react";

import type { BusinessEntityDetailVO } from "@/api/entities/type";
import type { OssFileUploadVO } from "@/api/file/type";
import { SectionTitle } from "@/components/DetailBlocks";
import { UploadFile } from "@/components/UploadFile";
import { resolveOssAccessUrl } from "@/components/UploadFile/utils";
import { useOssSignedUrl } from "@/hooks/useOssSignedUrl";

/** 营业执照单文件大小上限：10MB */
const LICENSE_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/** LicenseTab 入参 */
export interface LicenseTabProps {
  detail: BusinessEntityDetailVO | null;
  editMode?: boolean;
  licenseFileUrlDraft?: string;
  /** 编辑模式下上传/移除附件后更新证照 URL */
  onLicenseFileUrlChange?: (url: string) => void;
}

/** 将 URL 草稿转为 UploadFile 受控 value（仅有 url 时补全展示字段） */
const toLicenseUploadValue = (url: string): OssFileUploadVO | undefined => {
  if (!url) return undefined;
  return {
    url,
    objectKey: url,
    originalFilename: "营业执照副本",
    sizeBytes: 0,
  };
};

/** 证照管理 Tab：营业执照状态与照片预览 */
export const LicenseTab = ({
  detail,
  editMode = false,
  licenseFileUrlDraft = "",
  onLicenseFileUrlChange,
}: LicenseTabProps) => {
  /** 当前展示的证照 URL（编辑态读草稿，只读态读详情） */
  const licenseFileUrl = editMode ? licenseFileUrlDraft : (detail?.licenseFileUrl ?? "");
  /** 是否已上传营业执照 */
  const hasLicense = !!licenseFileUrl;
  /** 编辑模式下 UploadFile 受控 value */
  const licenseUploadValue = useMemo(
    () => toLicenseUploadValue(licenseFileUrlDraft),
    [licenseFileUrlDraft],
  );
  /** 只读模式下换取签名 URL，供图片预览使用 */
  const { signedUrl: licensePreviewUrl, loading: licensePreviewLoading } = useOssSignedUrl({
    fileUrl: detail?.licenseFileUrl,
    enabled: !editMode && !!detail?.licenseFileUrl,
  });

  /** 上传完成或移除附件后同步草稿 URL */
  const handleLicenseUploadChange = (value?: OssFileUploadVO | OssFileUploadVO[]) => {
    const file = Array.isArray(value) ? value[0] : value;
    onLicenseFileUrlChange?.(file?.url ?? "");
  };

  /** 新窗口打开原图（重新换取签名 URL，避免链接过期） */
  const handleOpenOriginalImage = async () => {
    if (!detail?.licenseFileUrl) return;
    const accessUrl = await resolveOssAccessUrl(detail.licenseFileUrl, "inline");
    window.open(accessUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-5">
      {editMode && (
        <Alert
          type="info"
          showIcon
          message="编辑模式：上传或替换营业执照照片，移除附件后保存将清除证照"
          className="text-sm mb-2!"
        />
      )}
      <div>
        <SectionTitle icon={Shield} title="营业执照信息" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
          <div className="flex items-center gap-1.5 text-sm min-w-0">
            <Shield className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-slate-500 w-24 shrink-0">证照状态</span>
            {detail?.licenseStatus ? (
              <Tag bordered className="text-[10px] m-0">
                {detail?.licenseStatus}
              </Tag>
            ) : (
              "—"
            )}
          </div>
          <div className="flex items-center gap-1.5 text-sm min-w-0">
            <Upload className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-slate-500 w-24 shrink-0">副本上传</span>
            {hasLicense ? (
              <Tag color="success" className="text-[10px] m-0 border-0">
                已上传
              </Tag>
            ) : (
              <Tag bordered className="text-[10px] m-0 text-slate-400">
                未上传
              </Tag>
            )}
          </div>
        </div>
      </div>
      <div>
        <SectionTitle icon={FileText} title="营业执照照片" />
        {editMode ? (
          <UploadFile
            value={licenseUploadValue}
            onChange={handleLicenseUploadChange}
            maxCount={1}
            alwaysShowDropZone
            variant="compact"
            ossType={1}
            accept="image/*"
            maxSizeBytes={LICENSE_MAX_SIZE_BYTES}
            dragHint="点击或拖拽上传营业执照"
            formatHint="支持 JPG/PNG 格式，最大 10MB"
          />
        ) : hasLicense ? (
          <div className="border rounded-lg overflow-hidden bg-slate-50/50">
            <div className="p-4 flex justify-center min-h-32">
              <Spin spinning={licensePreviewLoading}>
                {licensePreviewUrl ? (
                  <img
                    src={licensePreviewUrl}
                    alt="营业执照"
                    className="max-w-md max-h-64 object-contain rounded border border-slate-200 bg-white"
                  />
                ) : (
                  !licensePreviewLoading && (
                    <span className="text-sm text-slate-400">营业执照预览加载失败</span>
                  )
                )}
              </Spin>
            </div>
            <div className="p-3 flex items-center justify-between bg-white/80 border-t">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                <span>已上传 · 营业执照副本</span>
              </div>
              <Button
                type="link"
                size="small"
                className="h-6 text-xs p-0"
                disabled={!detail?.licenseFileUrl}
                onClick={() => void handleOpenOriginalImage()}
                icon={<ExternalLink className="w-3 h-3" />}
              >
                查看原图
              </Button>
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center text-slate-400 bg-slate-50/50">
            <Upload className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-sm m-0">暂未上传营业执照照片</p>
            <p className="text-xs mt-1 m-0">支持 JPG/PNG 格式，最大 10MB</p>
          </div>
        )}
      </div>
      {detail?.licenseExpiringSoon && (
        <Alert
          type="warning"
          showIcon
          icon={<AlertCircle className="w-4 h-4" />}
          message="请确保营业执照在有效期内，过期需及时更新"
        />
      )}
    </div>
  );
};
